import { useEffect, useMemo, useState } from "react";

import type { OrthoPresignedUrlItem } from "api/model";
import { useGetOrthoPresignedUrls } from "api/orthos/orthos";
import { useCogOrtho, type LocationOrtho } from "context/CogOrtho";
import type { ImageCornerCoordinates } from "context/CogOrtho/orthoAssets";
import { useOrthoWorkflows } from "context/OrthoWorkflow";

interface OrthoFootprintsResult {
  footprintsById: ReadonlyMap<number, ImageCornerCoordinates>;
  isPending: boolean;
}

/**
 * Ensures the ortho workflow metadata has footprints for the given orthos.
 * Reads the shared cache so show/hide does not clear known boundaries.
 */
export const useOrthoFootprints = (
  orthos: readonly LocationOrtho[],
  enabled: boolean,
): OrthoFootprintsResult => {
  const {
    metadata: { knownOrthoMeta, loadOrthoMeta, rememberOrthoBoundary },
  } = useOrthoWorkflows();
  const { sessions } = useCogOrtho();
  const orthoIds = orthos.map((ortho) => ortho.id);

  // Prefer session overlays when present; write into the stable analysis cache.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    for (const orthoId of orthoIds) {
      if (knownOrthoMeta[orthoId]?.boundary != null) {
        continue;
      }
      const coordinates = sessions[orthoId]?.overlay?.coordinates;
      if (coordinates != null) {
        rememberOrthoBoundary(orthoId, coordinates);
      }
    }
  }, [enabled, knownOrthoMeta, orthoIds, rememberOrthoBoundary, sessions]);

  const missingOrthoIds = orthoIds.filter((orthoId) => {
    if (knownOrthoMeta[orthoId]?.boundary != null) {
      return false;
    }
    // Let an in-flight visible session publish before a duplicate COG open.
    if (sessions[orthoId]?.isPending === true) {
      return false;
    }
    return true;
  });

  const { data: presignedUrls = [], isPending: isPresignedUrlsPending } =
    useGetOrthoPresignedUrls(
      { ortho_id: missingOrthoIds },
      {
        query: {
          enabled: enabled && missingOrthoIds.length > 0,
          select: (res): OrthoPresignedUrlItem[] =>
            res.status === 200 ? res.data : [],
          staleTime: 50 * 60 * 1000,
        },
      },
    );

  const urlByOrthoId = new Map<number, string>();
  for (const item of presignedUrls) {
    urlByOrthoId.set(item.id, item.presigned_url);
  }

  const [isLoadingFootprints, setIsLoadingFootprints] = useState(false);

  // Removingthis useMemo() caused more problems with compiler bailouts
  // and unstable depedencies, resulting in UI flicker.
  const footprintLoadKey = useMemo(
    () =>
      missingOrthoIds
        .map((orthoId) => `${orthoId}:${urlByOrthoId.get(orthoId) ?? ""}`)
        .join("|"),
    [
      missingOrthoIds,
      // React Compiler stabilizes this Map; oxlint cannot see that.
      // oxlint-disable-next-line react-hooks/exhaustive-deps
      urlByOrthoId,
    ],
  );

  useEffect(() => {
    if (!enabled || missingOrthoIds.length === 0) {
      setIsLoadingFootprints(false);
      return;
    }

    if (isPresignedUrlsPending) {
      setIsLoadingFootprints(true);
      return;
    }

    let isCancelled = false;

    const loadMissing = async () => {
      setIsLoadingFootprints(true);

      await Promise.all(
        missingOrthoIds.map(async (orthoId) => {
          const url = urlByOrthoId.get(orthoId);
          if (url == null) {
            return;
          }
          // Failures are logged by the provider.
          await loadOrthoMeta(orthoId, url);
        }),
      );

      if (!isCancelled) {
        setIsLoadingFootprints(false);
      }
    };

    void loadMissing();

    return () => {
      isCancelled = true;
    };
  }, [
    enabled,
    footprintLoadKey,
    isPresignedUrlsPending,
    loadOrthoMeta,
    missingOrthoIds,
    // React Compiler stabilizes this Map; oxlint cannot see that.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    urlByOrthoId,
  ]);

  const footprintsById = new Map<number, ImageCornerCoordinates>();
  for (const orthoId of orthoIds) {
    const coordinates = knownOrthoMeta[orthoId]?.boundary;
    if (coordinates != null) {
      footprintsById.set(orthoId, coordinates);
    }
  }

  const waitingOnSessions = orthoIds.some((orthoId) => {
    if (knownOrthoMeta[orthoId]?.boundary != null) {
      return false;
    }
    return sessions[orthoId]?.isPending === true;
  });

  const isPending =
    enabled &&
    (waitingOnSessions ||
      (missingOrthoIds.length > 0 &&
        (isPresignedUrlsPending || isLoadingFootprints)));

  return { footprintsById, isPending };
};
